import Card from '../../components/ui/Card';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { einheitenApi } from '../../api/einheiten';
import SortTh from '../../components/ui/SortTh';
import Spinner from '../../components/ui/Spinner';
import EmptyTableRow from '../../components/ui/EmptyTableRow';
import Pagination from '../../components/ui/Pagination';
import { useSessionFilter } from '../../hooks/useSessionFilter';
import { usePageNavigation } from '../../hooks/usePageNavigation';
import { useColumnConfig, useModuleVisibility } from '../../hooks/useConfigVisibility';

export default function ObjekteList() {
  const navigate = useNavigate();
  const { page, setPage, returnTo } = usePageNavigation();
  const [searchText, setSearchText] = useSessionFilter<string>('objekte_searchText', '');
  const [sortBy, setSortBy] = useSessionFilter<string>('objekte_sortBy', 'adresse');
  const [sortDir, setSortDir] = useSessionFilter<'asc' | 'desc'>('objekte_sortDir', 'asc');
  const cols = useColumnConfig('objekte');
  const pv = useModuleVisibility();
  const perPage = 50;

  const { data, isLoading } = useQuery({
    queryKey: ['einheiten', { page, per_page: perPage, search_text: searchText || undefined, sort_by: sortBy, sort_dir: sortDir }],
    queryFn: () => einheitenApi.getEinheiten({ page, per_page: perPage, search_text: searchText || undefined, sort_by: sortBy, sort_dir: sortDir }),
  });

  const totalPages = data ? Math.ceil(data.total / perPage) : 0;

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 py-6">
        <Card className="p-4 mb-4">
          <input
            type="text"
            placeholder="Suche nach Bezeichnung, Registrierungsnummer..."
            className="w-full border rounded-lg px-3 py-2 text-sm"
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
          />
        </Card>

        {isLoading ? (
          <Spinner />
        ) : (
          <>
            <Card className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortTh field="bezeichnung" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Bezeichnung</SortTh>
                    {cols.typ !== false && <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Typ</th>}
                    {cols.haus !== false && pv.haeuser !== false && <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Haus</th>}
                    {cols.adresse !== false && pv.adressen !== false && <SortTh field="adresse" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Adresse</SortTh>}
                    {cols.plz !== false && <SortTh field="plz" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>PLZ</SortTh>}
                    {cols.verfahren !== false && pv.verfahren !== false && <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Verfahren</th>}
                    {cols.inserate !== false && pv.inserate !== false && <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Inserate</th>}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data?.items?.map((e) => (
                    <tr
                      key={e.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/objekte/${e.id}?returnTo=${encodeURIComponent(returnTo)}`)}
                    >
                      <td className="px-3 py-3 text-sm font-medium text-gray-900">{e.bezeichnung}</td>
                      {cols.typ !== false && (
                        <td className="px-3 py-3 text-sm">
                          {e.typ ? (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-teal-100 text-teal-800">
                              {e.typ}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      )}
                      {cols.haus !== false && pv.haeuser !== false && (
                        <td className="px-3 py-3 text-sm">
                          <button
                            className="btn btn-text hover:underline"
                            onClick={(ev) => { ev.stopPropagation(); navigate(`/haeuser/${e.haus_id}`); }}
                          >
                            {e.haus_bezeichnung}
                          </button>
                        </td>
                      )}
                      {cols.adresse !== false && pv.adressen !== false && (
                        <td className="px-3 py-3 text-sm">
                          <button
                            className="btn btn-text hover:underline"
                            onClick={(ev) => { ev.stopPropagation(); navigate(`/adressen/${e.adresse_id}`); }}
                          >
                            {e.adresse_str}
                          </button>
                        </td>
                      )}
                      {cols.plz !== false && <td className="px-3 py-3 text-sm text-gray-500">{e.plz || '-'}</td>}
                      {cols.verfahren !== false && pv.verfahren !== false && (
                        <td className="px-3 py-3 text-sm">
                          {e.verfahren_count > 0 ? (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
                              {e.verfahren_count}
                            </span>
                          ) : (
                            <span className="text-gray-400">0</span>
                          )}
                        </td>
                      )}
                      {cols.inserate !== false && pv.inserate !== false && (
                        <td className="px-3 py-3 text-sm">
                          {e.inserate_count > 0 ? (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                              {e.inserate_count}
                            </span>
                          ) : (
                            <span className="text-gray-400">0</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                  {(!data?.items || data.items.length === 0) && (
                    <EmptyTableRow colSpan={7} message="Keine Objekte gefunden." />
                  )}
                </tbody>
              </table>
            </Card>

            <Pagination
              page={page}
              totalPages={totalPages}
              total={data?.total ?? 0}
              entityLabel="Objekte"
              onPageChange={setPage}
            />
          </>
        )}
      </main>
    </div>
  );
}
